import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { startWith } from 'rxjs';
import {
  FilterDropdownComponent,
  FilterDropdownOption,
} from '../../components/filter-dropdown/filter-dropdown.component';
import { ResetFilterButtonComponent } from '../../components/reset-filter-button/reset-filter-button.component';
import { TestPlan } from '../../models/test-plan.model';
import { TestPlanService } from '../../services/test-plan.service';
import { SavedViewService } from '../../services/saved-view.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { PageSizePreferenceService } from '../../services/page-size-preference.service';
import { SavedView } from '../../models/saved-view.model';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import { StatGroupComponent, StatItem } from '../../components/stat-group/stat-group.component';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { ProgressBarComponent } from '../../components/progress-bar/progress-bar.component';
import { CodeIdChipComponent } from '../../components/code-id-chip/code-id-chip.component';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { SavedViewOption, SavedViewsMenuComponent } from '../../shared/saved-views/saved-views-menu.component';
import { buildTestPlanSavedViewFiltersJson, parseTestPlanSavedViewFilters, writeErrorKey } from './test-plans.utils';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { ListFocusScrollDirective } from '../../shared/scroll-container/list-focus-scroll.directive';

type SortKey = 'publicId' | 'title' | 'release' | 'assignee' | 'dueDate';
type SortDirection = 'asc' | 'desc';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_ROUTE_KEY = 'test-plans';

@Component({
  selector: 'app-test-plans',
  imports: [
    ListFocusScrollDirective,
    CommonModule,
    TranslatePipe,
    DatePipe,
    MatIcon,
    SearchBarComponent,
    FilterDropdownComponent,
    ResetFilterButtonComponent,
    StatGroupComponent,
    ConfirmDialogComponent,
    PageHeaderComponent,
    ProgressBarComponent,
    CodeIdChipComponent,
    ProjectNavComponent,
    SavedViewsMenuComponent,
    AvatarComponent,
  ],
  templateUrl: './test-plans.component.html',
  styleUrl: './test-plans.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestPlansComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private service = inject(TestPlanService);
  private savedViewService = inject(SavedViewService);
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  private pageSizePref = inject(PageSizePreferenceService);
  private projectRoleService = inject(ProjectRoleService);

  readonly projectId: string;
  readonly plans = signal<TestPlan[]>([]);
  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly deleteError = signal(false);
  readonly deleteErrorKey = signal('testPlans.list.deleteError');
  readonly isReadOnly = computed(
    () => this.projectRoleService.isProjectReader() && !this.projectRoleService.isTenantAdmin(),
  );
  readonly deleting = signal(false);
  readonly planToDelete = signal<TestPlan | null>(null);

  readonly searchTerm = signal('');
  readonly assigneeFilter = signal<string | null>(null);

  /**
   * Alt-Ansichten aus Vor-NEX-1059-Zeiten koennen sowohl `query` als auch
   * `version` gesetzt haben (auf develop schrieb `onSavedViewSaved` beide
   * unabhaengig). Der Version-Chip ist entfallen, `version` bleibt unsichtbar —
   * ohne dieses Signal wuerde er beim Re-Save mit `null` ueberschrieben und die
   * gespeicherte Version waere weg (Datenverlust, Review-Nachfassung C1).
   * Wir spiegeln den Wert bei Lade-Zeit nur dann in die Suche, wenn `query` leer
   * ist; sonst tragen wir ihn hier durch bis zum naechsten Speichern.
   *
   * Lebensdauer: der Puffer gilt nur so lange, wie die geladene Alt-Ansicht die
   * aktive ist. Verlaesst der Nutzer sie durch eine manuelle Aenderung
   * (`clearActiveView`), wird der Puffer geleert — sonst wuerde er in eine neu
   * gespeicherte Ansicht wandern, die der Nutzer nie so gefiltert hat
   * (Review-Nachfassung 2, C1).
   */
  private readonly preservedVersion = signal<string | null>(null);
  readonly sortKey = signal<SortKey>('publicId');
  readonly sortDirection = signal<SortDirection>('desc');
  readonly pageIndex = signal(0);
  readonly pageSize = signal(this.pageSizePref.get(PAGE_SIZE_ROUTE_KEY, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE));
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  readonly availableAssignees = computed(() => {
    const map = new Map<string, string>();
    for (const plan of this.plans()) {
      if (plan.assigneeUserId) {
        map.set(plan.assigneeUserId, plan.assigneeName ?? plan.assigneeUserId);
      }
    }
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  });

  readonly assigneeDropdownOptions = computed<FilterDropdownOption[]>(() =>
    this.availableAssignees().map((a) => ({ value: a.id, label: a.name })),
  );

  readonly assigneeFilterValue = computed(() => this.assigneeFilter() ?? '');

  readonly hasFilters = computed(() => !!this.searchTerm() || this.assigneeFilter() !== null);

  readonly savedViews = signal<SavedView[]>([]);
  readonly activeSavedViewName = signal<string | null>(null);
  readonly savedViewsError = signal<string | null>(null);

  readonly savedViewsOptions = computed<SavedViewOption[]>(() =>
    this.savedViews().map((v) => ({ name: v.name, summary: this.summarizeSavedView(v) })),
  );

  private readonly langChange = toSignal(this.translate.onLangChange.pipe(startWith(null)), { initialValue: null });

  readonly stats = computed((): StatItem[] => {
    this.langChange();
    const all = this.plans();
    const active = all.filter((p) => p.status === 'AKTIV').length;
    const done = all.filter((p) => p.status === 'ABGESCHLOSSEN').length;
    return [
      { label: this.translate.instant('testPlans.stats.total'), value: all.length },
      { label: this.translate.instant('testPlans.stats.active'), value: active, color: '#1E40AF' },
      { label: this.translate.instant('testPlans.stats.done'), value: done, color: '#15803D' },
    ];
  });

  readonly filteredPlans = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    const assigneeF = this.assigneeFilter();
    const key = this.sortKey();
    const dir = this.sortDirection();

    let rows = this.plans();
    if (term) {
      rows = rows.filter(
        (p) =>
          p.title.toLowerCase().includes(term) ||
          (p.publicId ?? '').toLowerCase().includes(term) ||
          (p.releaseId ?? '').toLowerCase().includes(term) ||
          (p.releaseLabel ?? '').toLowerCase().includes(term),
      );
    }
    if (assigneeF) {
      rows = rows.filter((p) => p.assigneeUserId === assigneeF);
    }
    return [...rows].sort((a, b) => this.compare(a, b, key, dir));
  });

  readonly total = computed(() => this.filteredPlans().length);
  readonly unfilteredTotal = computed(() => this.plans().length);
  readonly resultCountKey = computed(() =>
    this.unfilteredTotal() === 1 ? 'testPlans.list.resultCountOne' : 'testPlans.list.resultCountOther',
  );
  readonly pageCount = computed(() => Math.max(1, Math.ceil(this.total() / this.pageSize())));
  readonly rangeStart = computed(() => (this.total() === 0 ? 0 : this.pageIndex() * this.pageSize() + 1));
  readonly rangeEnd = computed(() => Math.min(this.total(), (this.pageIndex() + 1) * this.pageSize()));

  readonly pagedPlans = computed(() => {
    const start = this.pageIndex() * this.pageSize();
    return this.filteredPlans().slice(start, start + this.pageSize());
  });

  constructor() {
    this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
    this.loadPlans();
    this.loadSavedViews();
  }

  loadPlans(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.service
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (plans) => {
          this.plans.set(plans);
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private loadSavedViews(): void {
    this.savedViewService
      .list(this.projectId, 'TEST_PLANS')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (views) => this.savedViews.set(views),
        error: () => this.savedViews.set([]),
      });
  }

  private summarizeSavedView(view: SavedView): string {
    const filters = parseTestPlanSavedViewFilters(view.filtersJson);
    const assigneeName = filters.assignee
      ? (this.availableAssignees().find((a) => a.id === filters.assignee)?.name ?? filters.assignee)
      : null;
    // Legacy-Ansicht mit ausschliesslich version: sonst waere die Zusammenfassung
    // "Alle", obwohl die Auswahl filtert (NEX-1059-Review Info).
    const effectiveQuery = filters.query ?? filters.version;
    const parts = [effectiveQuery, assigneeName].filter((s): s is string => !!s);
    if (parts.length === 0) {
      return this.translate.instant('testPlans.savedViews.summaryAll');
    }
    return parts.join(' · ');
  }

  onSavedViewSelected(name: string): void {
    const view = this.savedViews().find((v) => v.name === name);
    if (!view) return;
    const filters = parseTestPlanSavedViewFilters(view.filtersJson);
    // Drei Faelle einer Alt-Ansicht (NEX-1059-Review C1):
    //   query gesetzt, version gesetzt   -> query gewinnt sichtbar,
    //                                       version bleibt unsichtbar im preservedVersion-Puffer.
    //   query leer,   version gesetzt   -> version in die Suche spiegeln
    //                                       (Migration; die Suche matcht das Release).
    //   sonst                            -> beides leer.
    // Wichtig ist, dass beim Re-Save kein gespeicherter Wert stumm ueberschrieben
    // wird, den der Nutzer nie in einem Bedienelement gesehen hat.
    if (filters.query) {
      this.searchTerm.set(filters.query);
      this.preservedVersion.set(filters.version);
    } else if (filters.version) {
      this.searchTerm.set(filters.version);
      this.preservedVersion.set(null);
    } else {
      this.searchTerm.set('');
      this.preservedVersion.set(null);
    }
    this.assigneeFilter.set(filters.assignee);
    this.pageIndex.set(0);
    this.activeSavedViewName.set(view.name);
  }

  onSavedViewSaved(name: string): void {
    const filtersJson = buildTestPlanSavedViewFiltersJson({
      query: this.searchTerm() || null,
      // Alt-Wert unveraendert durchreichen, wenn er dem Nutzer nicht angezeigt
      // wurde. Neue Ansichten haben preservedVersion=null und schreiben null.
      version: this.preservedVersion(),
      assignee: this.assigneeFilter(),
    });
    this.savedViewService.upsert(this.projectId, { name, context: 'TEST_PLANS', filtersJson }).subscribe({
      next: (saved) => {
        this.savedViews.update((list) => {
          const rest = list.filter((v) => v.name !== saved.name);
          return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
        });
        this.activeSavedViewName.set(saved.name);
        this.savedViewsError.set(null);
      },
      error: () => this.savedViewsError.set(this.translate.instant('testPlans.savedViews.saveError')),
    });
  }

  /**
   * Das Loeschen selbst uebernimmt der generische SavedViewsMenuComponent (NEX-786) —
   * hier wird nur noch der lokale State nach einem erfolgreichen Loeschvorgang bereinigt.
   */
  onSavedViewDeleted(name: string): void {
    this.savedViews.update((list) => list.filter((v) => v.name !== name));
    if (this.activeSavedViewName() === name) {
      // clearActiveView() statt aktivName direkt zu leeren — sonst ueberlebt
      // preservedVersion die geloeschte Ansicht und wandert beim naechsten
      // Speichern in eine neue Ansicht, die der Nutzer nie so gefiltert hat
      // (Review-Nachfassung, sgraetz).
      this.clearActiveView();
    }
  }

  onSavedViewDeleteFailed(): void {
    this.savedViewsError.set(this.translate.instant('testPlans.savedViews.deleteError'));
  }

  private clearActiveView(): void {
    if (this.activeSavedViewName() !== null) {
      this.activeSavedViewName.set(null);
    }
    // Der Legacy-Version-Puffer gehoert zur geladenen Ansicht; sobald der Nutzer
    // sie manuell verlaesst, darf er nicht in eine neu gespeicherte Ansicht
    // wandern (Review-Nachfassung 2, C1).
    this.preservedVersion.set(null);
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.pageIndex.set(0);
    this.clearActiveView();
  }

  setAssigneeFilter(id: string | null): void {
    this.assigneeFilter.set(id || null);
    this.pageIndex.set(0);
    this.clearActiveView();
  }

  resetFilters(): void {
    this.searchTerm.set('');
    this.assigneeFilter.set(null);
    this.pageIndex.set(0);
    this.clearActiveView();
  }

  toggleSort(key: SortKey): void {
    if (this.sortKey() === key) {
      this.sortDirection.update((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      this.sortKey.set(key);
      this.sortDirection.set('asc');
    }
    this.pageIndex.set(0);
  }

  onPageChange(index: number): void {
    this.pageIndex.set(index);
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.pageIndex.set(0);
    this.pageSizePref.set(PAGE_SIZE_ROUTE_KEY, size, PAGE_SIZE_OPTIONS);
  }

  ariaSort(key: SortKey): 'ascending' | 'descending' | 'none' {
    if (this.sortKey() !== key) return 'none';
    return this.sortDirection() === 'asc' ? 'ascending' : 'descending';
  }

  onCreate(): void {
    this.router.navigate(['/projects', this.projectId, 'test-plans', 'new']);
  }

  onEdit(plan: TestPlan, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/projects', this.projectId, 'test-plans', plan.id, 'edit']);
  }

  onOpenRow(plan: TestPlan): void {
    if (this.isReadOnly()) {
      this.onOpenReport(plan);
    } else {
      this.onEdit(plan);
    }
  }

  onRowKeydown(event: Event, plan: TestPlan): void {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.onOpenRow(plan);
  }

  onOpenReport(plan: TestPlan, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/projects', this.projectId, 'test-plans', plan.id, 'report']);
  }

  onAskDelete(plan: TestPlan, event: Event): void {
    event.stopPropagation();
    this.deleteError.set(false);
    this.planToDelete.set(plan);
  }

  onCancelDelete(): void {
    if (this.deleting()) return;
    this.planToDelete.set(null);
  }

  onConfirmDelete(): void {
    const plan = this.planToDelete();
    if (!plan) return;
    this.deleting.set(true);
    this.deleteError.set(false);
    this.service
      .delete(this.projectId, plan.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.plans.update((list) => list.filter((p) => p.id !== plan.id));
          this.deleting.set(false);
          this.planToDelete.set(null);
        },
        error: (err: unknown) => {
          this.deleting.set(false);
          this.deleteErrorKey.set(writeErrorKey(err, 'testPlans.list.deleteError'));
          this.deleteError.set(true);
        },
      });
  }

  progressPercent(plan: TestPlan): number {
    const total = plan.progress.total || 1;
    return Math.round(((plan.progress.passed + plan.progress.failed) / total) * 100);
  }

  trackById(_: number, plan: TestPlan): string {
    return plan.id;
  }

  private compare(a: TestPlan, b: TestPlan, key: SortKey, dir: SortDirection): number {
    const av = this.sortValue(a, key);
    const bv = this.sortValue(b, key);
    if (av === bv) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = av < bv ? -1 : 1;
    return dir === 'asc' ? cmp : -cmp;
  }

  private sortValue(plan: TestPlan, key: SortKey): string | number | null {
    switch (key) {
      case 'publicId':
        return plan.publicId ?? '';
      case 'title':
        return plan.title.toLowerCase();
      case 'release':
        return plan.releaseId?.toLowerCase() ?? null;
      case 'assignee':
        return plan.assigneeName?.toLowerCase() ?? null;
      case 'dueDate':
        return plan.dueDate ?? null;
    }
  }
}
